<template>
  <div class="s-canvas" @mouseenter="hovering = true" @mouseleave="hovering = false">
    <canvas ref="canvas"
            :width="contentWidth"
            :height="contentHeight"></canvas>
    <div class="refresh-overlay" :class="{ visible: hovering }">
      <i class="el-icon-refresh"></i>
      <span>换一个</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SIdentify',
  props: {
    fresh: {
      type: Boolean,
      default: true
    },
    contentWidth: {
      type: Number,
      default: 120
    },
    contentHeight: {
      type: Number,
      default: 48
    }
  },
  data() {
    return {
      identifyCodes: '0123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz',
      identifyCode: '',
      hovering: false,
      // 现代配色方案 - 多组渐变色
      colorSchemes: [
        { bg: ['#667eea', '#764ba2'], text: '#ffffff' },
        { bg: ['#f093fb', '#f5576c'], text: '#ffffff' },
        { bg: ['#4facfe', '#00f2fe'], text: '#ffffff' },
        { bg: ['#43e97b', '#38f9d7'], text: '#ffffff' },
        { bg: ['#fa709a', '#fee140'], text: '#ffffff' },
        { bg: ['#a18cd1', '#fbc2eb'], text: '#4a4a6a' },
        { bg: ['#ffecd2', '#fcb69f'], text: '#8b5e3c' },
        { bg: ['#89f7fe', '#66a6ff'], text: '#ffffff' }
      ]
    };
  },
  methods: {
    randomNum(min, max) {
      return Math.floor(Math.random() * (max - min) + min);
    },

    drawPic() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const w = this.contentWidth;
      const h = this.contentHeight;

      // 选择一组配色
      const scheme = this.colorSchemes[this.randomNum(0, this.colorSchemes.length)];

      // 绘制渐变背景
      const gradient = ctx.createLinearGradient(0, 0, w, h);
      gradient.addColorStop(0, scheme.bg[0]);
      gradient.addColorStop(1, scheme.bg[1]);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, h);

      // 绘制装饰性圆形（半透明）
      this.drawDecoCircles(ctx, w, h);

      // 绘制干扰曲线（更优雅）
      this.drawCurves(ctx, w, h);

      // 绘制验证码文字
      for (let i = 0; i < this.identifyCode.length; i++) {
        this.drawText(ctx, this.identifyCode[i], i, scheme.text, w, h);
      }
    },

    drawText(ctx, txt, i, color, w, h) {
      const fontSize = this.randomNum(20, 26);
      ctx.font = `bold ${fontSize}px 'Courier New', 'SF Mono', Consolas, monospace`;

      // 文字带半透明阴影
      ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
      ctx.shadowBlur = 2;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;

      ctx.fillStyle = color;
      ctx.textBaseline = 'middle';

      const charWidth = w / (this.identifyCode.length + 1);
      const x = (i + 0.7) * charWidth;
      const y = h / 2 + this.randomNum(-4, 4);
      const deg = this.randomNum(-15, 15);

      ctx.translate(x, y);
      ctx.rotate((deg * Math.PI) / 180);
      ctx.fillText(txt, 0, 0);
      ctx.rotate((-deg * Math.PI) / 180);
      ctx.translate(-x, -y);

      // 重置阴影
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    },

    drawCurves(ctx, w, h) {
      // 绘制 2 条优雅的贝塞尔曲线
      for (let i = 0; i < 2; i++) {
        ctx.strokeStyle = `rgba(255, 255, 255, ${0.2 + Math.random() * 0.15})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(this.randomNum(0, 10), this.randomNum(0, h));
        ctx.bezierCurveTo(
          this.randomNum(w * 0.2, w * 0.4), this.randomNum(0, h),
          this.randomNum(w * 0.6, w * 0.8), this.randomNum(0, h),
          this.randomNum(w - 10, w), this.randomNum(0, h)
        );
        ctx.stroke();
      }
    },

    drawDecoCircles(ctx, w, h) {
      // 绘制几个半透明装饰圆
      for (let i = 0; i < 4; i++) {
        ctx.fillStyle = `rgba(255, 255, 255, ${0.06 + Math.random() * 0.08})`;
        ctx.beginPath();
        ctx.arc(
          this.randomNum(0, w),
          this.randomNum(0, h),
          this.randomNum(8, 20),
          0,
          2 * Math.PI
        );
        ctx.fill();
      }
    },

    makeCode(o, l) {
      this.identifyCode = '';
      for (let i = 0; i < l; i++) {
        this.identifyCode += this.identifyCodes[
          this.randomNum(0, this.identifyCodes.length)
        ];
      }

      this.$nextTick(() => {
        this.drawPic();
      });

      this.$emit('makedCode', this.identifyCode);
    }
  },
  watch: {
    fresh() {
      this.makeCode(this.identifyCodes, 4);
    }
  }
};
</script>

<style scoped>
.s-canvas {
  position: relative;
  display: inline-block;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.s-canvas:hover {
  transform: scale(1.02);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
}

.s-canvas canvas {
  display: block;
  border-radius: 8px;
}

.refresh-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.25s ease;
  border-radius: 8px;
  pointer-events: none;
}

.refresh-overlay.visible {
  opacity: 1;
}

.refresh-overlay i {
  color: #fff;
  font-size: 14px;
}

.refresh-overlay span {
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 1px;
}
</style>
